import React, { useState, useEffect } from 'react';
import PageLayout from '../../../shared/components/PageLayout';
import {
    TextField, Button, CircularProgress, Box, Typography, Snackbar, Alert,
    Tabs, Tab, Checkbox, Avatar, Chip, Paper, Divider,
} from '@mui/material';
import { FaBirthdayCake } from 'react-icons/fa';
import { firebaseAuth } from '../../../context/Firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import useMassMessageViewModel from '../hooks/useMassMessageViewModel';

const DEFAULT_BIRTHDAY_MSG = '🎂 Happy Birthday! Wishing you a wonderful day filled with joy and happiness!';

function UserCheckList({ users, selected, onToggle }) {
    return (
        <Paper variant="outlined" sx={{ maxHeight: 280, overflowY: 'auto', p: 1 }}>
            {users.length === 0 && (
                <Typography variant="body2" color="textSecondary" sx={{ p: 1 }}>No users found.</Typography>
            )}
            {users.map(user => (
                <Box key={user.uid} sx={{ display: 'flex', alignItems: 'center', py: 0.5 }}>
                    <Checkbox
                        checked={selected.includes(user.uid)}
                        onChange={() => onToggle(user.uid)}
                        size="small"
                    />
                    <Avatar src={user.profilePic} sx={{ width: 28, height: 28, mr: 1, fontSize: 12 }}>
                        {user.name?.[0]}
                    </Avatar>
                    <Box>
                        <Typography variant="body2">{user.name}</Typography>
                        <Typography variant="caption" color="textSecondary">{user.email}</Typography>
                    </Box>
                </Box>
            ))}
        </Paper>
    );
}

const MassMessage = () => {
    const [tab, setTab] = useState(0); // 0=All, 1=Selected, 2=Birthday
    const [messageText, setMessageText] = useState('');
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [authUser, authLoading] = useAuthState(firebaseAuth);

    const currentUserId = authUser?.uid;
    const currentUserName = authUser?.displayName || authUser?.email;

    const {
        loading, error, success, users, birthdayUsers, usersLoading,
        sendMassMessage, sendToSelected, sendBirthdayMessages, setError, setSuccess,
    } = useMassMessageViewModel(currentUserId, currentUserName);

    // Pre-fill birthday message when switching to birthday tab
    useEffect(() => {
        if (tab === 2) setMessageText(DEFAULT_BIRTHDAY_MSG);
        else setMessageText('');
    }, [tab]);

    useEffect(() => {
        if (error) {
            setSnackbar({ open: true, message: error, severity: 'error' });
            setError(null);
        }
        if (success) {
            setSnackbar({ open: true, message: 'Message(s) sent successfully!', severity: 'success' });
            setMessageText(tab === 2 ? DEFAULT_BIRTHDAY_MSG : '');
            setSelectedUsers([]);
            setSuccess(false);
        }
    }, [error, success, setError, setSuccess, tab]);

    const toggleUser = (uid) => {
        setSelectedUsers(prev => prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]);
    };

    const toggleAll = () => {
        setSelectedUsers(prev => prev.length === users.length ? [] : users.map(u => u.uid));
    };

    const handleSend = async () => {
        if (tab === 0) await sendMassMessage(messageText);
        else if (tab === 1) await sendToSelected(selectedUsers, messageText);
        else await sendBirthdayMessages(messageText);
    };

    const sendLabel = () => {
        if (loading) return <CircularProgress size={22} color="inherit" />;
        if (tab === 0) return `Send to All Users (${users.length})`;
        if (tab === 1) return `Send to Selected (${selectedUsers.length})`;
        return `Send Birthday Messages (${birthdayUsers.length})`;
    };

    if (authLoading) return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;

    return (
        <PageLayout title="Messaging">
        <Box sx={{ maxWidth: 700 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Send messages to all users, selected users, or birthday users.
            </Typography>

            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                <Tab label="All Users" />
                <Tab label="Select Users" />
                <Tab label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <FaBirthdayCake />
                        <span>Birthday</span>
                        {birthdayUsers.length > 0 && (
                            <Chip label={birthdayUsers.length} size="small" color="error" sx={{ ml: 0.5, height: 18, fontSize: 11 }} />
                        )}
                    </Box>
                } />
            </Tabs>

            {/* Tab: All Users */}
            {tab === 0 && (
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    This will send the message to all {usersLoading ? '...' : users.length} registered users.
                </Typography>
            )}

            {/* Tab: Select Users */}
            {tab === 1 && (
                <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="body2" color="textSecondary">
                            {selectedUsers.length} of {users.length} selected
                        </Typography>
                        <Button size="small" onClick={toggleAll} disabled={usersLoading}>
                            {selectedUsers.length === users.length ? 'Deselect All' : 'Select All'}
                        </Button>
                    </Box>
                    {usersLoading ? <CircularProgress size={20} /> : (
                        <UserCheckList users={users} selected={selectedUsers} onToggle={toggleUser} />
                    )}
                </Box>
            )}

            {/* Tab: Birthday */}
            {tab === 2 && (
                <Box sx={{ mb: 2 }}>
                    {usersLoading ? <CircularProgress size={20} /> : birthdayUsers.length === 0 ? (
                        <Alert severity="info">No users have a birthday today.</Alert>
                    ) : (
                        <>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                                {birthdayUsers.length} user(s) celebrating a birthday today:
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
                                {birthdayUsers.map(u => (
                                    <Chip key={u.uid} avatar={<Avatar src={u.profilePic}>{u.name?.[0]}</Avatar>} label={u.name} size="small" />
                                ))}
                            </Box>
                        </>
                    )}
                </Box>
            )}

            <Divider sx={{ mb: 2 }} />

            <TextField
                fullWidth
                multiline
                rows={5}
                label="Message"
                variant="outlined"
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                sx={{ mb: 2 }}
                disabled={loading}
            />

            <Button
                variant="contained"
                color={tab === 2 ? 'error' : 'primary'}
                onClick={handleSend}
                disabled={loading || !messageText.trim() || (tab === 1 && selectedUsers.length === 0) || (tab === 2 && birthdayUsers.length === 0)}
                fullWidth
            >
                {sendLabel()}
            </Button>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={5000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
        </PageLayout>
    );
};

export default MassMessage;
